/**
 * zplModel — the label designer's document model + ZPL compiler/parser.
 *
 * The ELEMENT MODEL (JSON) is the source of truth for kasvar (mobileZPLcode)
 * layouts; the ZPL is compiled output, regenerated on every save. The
 * compiler mirrors the Android Fr3ToZplConverter's command shapes
 * (MyFasManMobile Fr3ToZplConverter.java) so devices render both paths the
 * same way.
 *
 * Geometry is stored in MILLIMETRES (floats); dots = mm * dpmm at compile.
 * Font sizes are stored in printer DOTS (what ^A0 takes).
 *
 * Model shape:
 * {
 *   widthMm, heightMm, dpmm,
 *   rawHeader,   // optional: imported printer-config commands (~SD/^MT...), re-emitted verbatim
 *   elements: [
 *     { id, type:'text',    x, y, w, h, text, font, fontW, align, maxLines, rotation },
 *     { id, type:'barcode', x, y, w, h, data, barType, interpretation, module, rotation },
 *     { id, type:'box',     x, y, w, h, thickness, fill },   // fill: 'none'|'white'
 *     { id, type:'logo',    x, y, w, h },                    // -> [LOGOIMG:x,y,w,h]
 *   ]
 * }
 */

/** The tokens the classic kasvar print path substitutes (LabelPrinter.java:184-199). */
export const CLASSIC_TOKENS = [
    'TITLE', 'LINE1', 'LINE2', 'LINE3', 'LINE4',
    'SIZE', 'BARCODE', 'PRICE', 'DETAIL4', 'SEASON', 'DECIMAL',
];

/** Sample values for the WYSIWYG preview (and the Labelary render). */
export const SAMPLE_VALUES = {
    TITLE: 'BOOT CARDIGAN',
    LINE1: '104 CARDIGAN WOL',
    LINE2: 'Navy 46',
    LINE3: 'Dames Truien',
    LINE4: 'Marineblauw',
    SIZE: 'S/M',
    BARCODE: '5400000123457',
    PRICE: '69,95',
    DETAIL4: 'W26',
    SEASON: 'W26',
    DECIMAL: '95',
};

export const BAR_TYPES = ['EAN13', 'EAN8', 'CODE128', 'CODE39'];

let idCounter = 1;
export function nextId() {
    idCounter += 1;
    return 'el' + Date.now().toString(36) + '_' + idCounter;
}

export function emptyModel() {
    return { widthMm: 50, heightMm: 25, dpmm: 8, elements: [] };
}

export function newElement(type, model) {
    const base = { id: nextId(), type, x: 2, y: 2 };
    switch (type) {
        case 'text':
            return Object.assign(base, {
                w: Math.max(10, (model.widthMm || 50) - 4), h: 4,
                text: 'Tekst', font: 24, fontW: 24, align: 'L', maxLines: 1, rotation: 'N',
            });
        case 'barcode':
            return Object.assign(base, {
                w: Math.min(34, (model.widthMm || 50) - 4), h: 10,
                data: '[BARCODE]', barType: 'EAN13', interpretation: true, module: 2, rotation: 'N',
                background: true,
            });
        case 'box':
            return Object.assign(base, { w: 20, h: 10, thickness: 2, fill: 'none' });
        case 'line':
            // A line IS a box with one 0 dimension (^GBw,0,t) — separate
            // toolbox entry, same element type.
            return Object.assign(base, { type: 'box', w: 24, h: 0, thickness: 2, fill: 'none' });
        case 'logo':
            return Object.assign(base, { w: 16, h: 8 });
        default:
            return Object.assign(base, { w: 10, h: 5 });
    }
}

// ---------------------------------------------------------------------------
//  COMPILE  (model -> ZPL)
// ---------------------------------------------------------------------------

/** Same escaping the Android converter uses for literal text. */
export function escapeZpl(s) {
    return String(s)
        .replace(/\\/g, '\\\\')
        .replace(/\^/g, '\\5E')
        .replace(/~/g, '\\7E');
}

function dots(mm, dpmm) {
    return Math.round(mm * dpmm);
}

export function compile(model) {
    const dpmm = model.dpmm || 8;
    const lines = [];
    lines.push('^XA');
    // Printer-config commands captured from an imported label (ZebraDesigner
    // exports ship a whole ~TA/~SD/^MT... block; darkness, media mode and
    // print rate change the physical output, so they must survive the
    // round-trip). Emitted before ^CI28 so our charset always wins.
    if (model.rawHeader) lines.push(model.rawHeader);
    lines.push('^CI28'); // UTF-8 input so accented characters print
    lines.push('^PW' + dots(model.widthMm, dpmm));
    lines.push('^LL' + dots(model.heightMm, dpmm));

    (model.elements || []).forEach((el) => {
        const x = dots(el.x, dpmm);
        const y = dots(el.y, dpmm);
        const w = dots(el.w || 0, dpmm);
        const h = dots(el.h || 0, dpmm);

        // Opaque background: a white-filled box at the element's own origin,
        // emitted right before it, so bars/graphics beneath cannot bleed
        // through (ZPL prints in command order; ,W erases). The parser merges
        // this pair back into `background: true` on re-open.
        if ((el.type === 'text' || el.type === 'barcode') && el.background && w > 0 && h > 0) {
            lines.push('^FO' + x + ',' + y + '^GB' + w + ',' + h + ',' + Math.max(w, h) + ',W^FS');
        }

        if (el.type === 'text') {
            const font = Math.max(6, Math.round(el.font || 24));
            const fontW = Math.max(6, Math.round(el.fontW || el.font || 24));
            const maxLines = Math.min(9, Math.max(1, el.maxLines || 1));
            const align = 'LCRJ'.indexOf(el.align) >= 0 ? el.align : 'L';
            const rot = 'NRIB'.indexOf(el.rotation) >= 0 ? el.rotation : 'N';
            lines.push(
                '^FO' + x + ',' + y +
                '^A0' + rot + ',' + font + ',' + fontW +
                '^FB' + Math.max(w, font) + ',' + maxLines + ',0,' + align + ',0' +
                '^FD' + escapeZpl(el.text || '') + '^FS'
            );
        } else if (el.type === 'barcode') {
            const rot = 'NRIB'.indexOf(el.rotation) >= 0 ? el.rotation : 'N';
            const interp = el.interpretation ? 'Y' : 'N';
            // Mirror the Android converter: the interpretation line prints
            // BELOW the bars, so shrink them to keep the total in the box.
            let barH = h - (el.interpretation ? 28 : 0);
            if (barH < 20) barH = 20;
            const module = Math.min(4, Math.max(1, Math.round(el.module || 2)));
            let cmd = '^FO' + x + ',' + y + '^BY' + module + '\n';
            switch (el.barType) {
                case 'EAN13':
                    cmd += '^BE' + rot + ',' + barH + ',' + interp + ',N';
                    break;
                case 'EAN8':
                    cmd += '^B8' + rot + ',' + barH + ',' + interp + ',N';
                    break;
                case 'CODE39':
                    cmd += '^B3' + rot + ',N,' + barH + ',' + interp + ',N';
                    break;
                case 'CODE128':
                default:
                    cmd += '^BC' + rot + ',' + barH + ',' + interp + ',N,N';
                    break;
            }
            cmd += '^FD' + (el.data || '[BARCODE]') + '^FS';
            lines.push(cmd);
        } else if (el.type === 'box') {
            if (el.fill === 'white') {
                // Filled white mask (the fr3 "digits over bars" trick).
                lines.push('^FO' + x + ',' + y + '^GB' + Math.max(1, w) + ',' + Math.max(1, h) + ',' + Math.max(w, h) + ',W^FS');
            } else {
                // w or h of 0 is a LINE (^GBw,0,t horizontal / ^GB0,h,t
                // vertical) — the same shape the Android converter emits for
                // fr3 frame edges.
                lines.push('^FO' + x + ',' + y + '^GB' + w + ',' + h + ',' + Math.max(1, Math.round(el.thickness || 1)) + '^FS');
            }
        } else if (el.type === 'logo') {
            // The app injects the ticket logo as ^GF into these bounds
            // (LabelPrinter.substituteLogoMarkers).
            lines.push('[LOGOIMG:' + x + ',' + y + ',' + w + ',' + h + ']');
        }
    });

    lines.push('^XZ');
    return lines.join('\n') + '\n';
}

/** Substitute [TOKEN]s with sample values (preview / Labelary render). */
export function substituteSample(zpl) {
    let out = zpl;
    Object.keys(SAMPLE_VALUES).forEach((token) => {
        out = out.split('[' + token + ']').join(SAMPLE_VALUES[token]);
    });
    // A logo marker renders as nothing at Labelary; drop it for the preview.
    out = out.replace(/\[LOGOIMG:\d+,\d+,\d+,\d+\]\n?/g, '');
    // Unknown tokens blank out, like the device print path does.
    out = out.replace(/\[[A-Z0-9_]{2,}\]/g, '');
    return out;
}

// ---------------------------------------------------------------------------
//  PARSE  (ZPL subset -> model)  — for kasvars saved without a designer model
// ---------------------------------------------------------------------------

/**
 * Parse the command subset our compiler (and the Android fr3 converter)
 * emits. Anything else returns { ok:false } and the screen opens in raw-ZPL
 * mode instead of silently mangling a hand-tuned layout.
 */
export function parse(zpl, dpmm) {
    if (!zpl || typeof zpl !== 'string') return { ok: false, reason: 'empty' };
    const d = dpmm || 8;
    const model = { widthMm: 50, heightMm: 25, dpmm: d, elements: [] };

    let src = zpl
        .replace(/\|BCONLY\|/g, '')
        .replace(/\|\/BCONLY\|/g, '')
        .replace(/\r/g, '')
        .trim();

    // ZebraDesigner preamble that (re)sets the control characters to their
    // DEFAULTS (CT~ = tilde, CD, = delimiter, CC^ = caret) - pure no-ops for
    // us. A preamble that picks NON-default characters stays unsupported and
    // falls through to the raw editor.
    src = src.replace(/^(?:\s*CT~~CD,~CC\^~CT~)+\s*/, '');

    // Printer-config commands (ZebraDesigner emits them as a separate leading
    // ^XA...^XZ block): captured VERBATIM into model.rawHeader so darkness /
    // media mode / print rate keep printing identically, invisible in the
    // visual editor. ^CI is deliberately NOT captured (compile emits ^CI28).
    const headerParts = [];
    src = src.replace(/~TA\d+|~JS[A-Z]|\^LT-?\d+|\^MN[A-Z](?:,\d+)?|\^MT[A-Z]|\^PO[A-Z]|\^PM[A-Z]|\^JM[A-Z]|~SD\d+|\^JU[A-Z]|\^LR[A-Z]|\^MM[A-Z](?:,[NY])?|\^MD-?\d+|\^PR[A-Z0-9,]+|\^LH\d+,\d+/g,
        (m) => { headerParts.push(m); return ''; });
    // Quantity + non-zero label shift stay out of the model: quantity is a
    // runtime decision of the print path, and ^LS0 is the default anyway.
    src = src.replace(/\^PQ[\d,]*[NY]?/g, '').replace(/\^LS0(?=\D|$)/g, '');
    if (headerParts.length) model.rawHeader = headerParts.join('');

    // Logo markers are standalone lines.
    src = src.replace(/\[LOGOIMG:(\d+),(\d+),(\d+),(\d+)\]/g, (m, x, y, w, h) => {
        model.elements.push({
            id: nextId(), type: 'logo',
            x: +x / d, y: +y / d, w: +w / d, h: +h / d,
        });
        return '';
    });

    // Header/footer + global commands we accept and strip.
    src = src.replace(/\^XA/g, '').replace(/\^XZ/g, '');
    src = src.replace(/\^CI\d+/g, '');
    const pw = src.match(/\^PW(\d+)/);
    if (pw) model.widthMm = round2(+pw[1] / d);
    const ll = src.match(/\^LL(\d+)/);
    if (ll) model.heightMm = round2(+ll[1] / d);
    src = src.replace(/\^PW\d+/g, '').replace(/\^LL\d+/g, '');

    // A leading ^BY that applies to the following barcode may sit before ^FO.
    let pendingModule = null;

    // Split into ^FO/^FT segments; everything left must be whitespace.
    // ^FT anchors the field at its BASELINE (text) / bottom edge (bars,
    // boxes) instead of the top-left corner - imported layouts (ZebraDesigner
    // uses ^FT throughout) convert to top-left once the element's height is
    // known, further down.
    const segments = [];
    const foRe = /\^F([OT])(\d+),(\d+)/g;
    let match = foRe.exec(src);
    let lastEnd = 0;
    let head = match ? src.slice(0, match.index) : src;
    const headBy = head.match(/\^BY(\d)/);
    if (headBy) { pendingModule = +headBy[1]; head = head.replace(/\^BY\d/g, ''); }
    if (head.trim() !== '') return { ok: false, reason: 'unrecognised: ' + head.trim().slice(0, 40) };
    while (match) {
        const start = match.index;
        const next = foRe.exec(src);
        segments.push({
            baseline: match[1] === 'T',
            x: +match[2], y: +match[3],
            body: src.slice(start, next ? next.index : src.length).replace(/^\^F[OT]\d+,\d+/, ''),
        });
        lastEnd = next ? next.index : src.length;
        match = next;
    }
    void lastEnd;

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        let body = seg.body;
        const el = { id: nextId(), x: round2(seg.x / d), y: round2(seg.y / d) };

        const by = body.match(/\^BY(\d)/);
        if (by) { pendingModule = +by[1]; body = body.replace(/\^BY\d/g, ''); }

        const bar = body.match(/\^(BE|B8|B3|BC)([NRIB])(?:,N)?,(\d+),([YN])/);
        const gb = body.match(/\^GB(\d+),(\d+),(\d+)(?:,([BW]))?(?:,\d)?\^FS/);
        const fd = body.match(/\^FD([\s\S]*?)\^FS/);
        const font = body.match(/\^A0([NRIB]),(\d+)(?:,(\d+))?/) || body.match(/\^A([A-Z])([NRIB]),(\d+)(?:,(\d+))?/);
        const fb = body.match(/\^FB(\d+),(\d+),(-?\d+),([LCRJ]),(\d+)/);

        if (bar && fd) {
            el.type = 'barcode';
            el.data = fd[1].trim();
            el.rotation = bar[2];
            el.interpretation = bar[4] === 'Y';
            const barH = +bar[3];
            el.h = round2((barH + (el.interpretation ? 28 : 0)) / d);
            el.module = pendingModule || 2;
            const modules = bar[1] === 'BE' ? 95 : (bar[1] === 'B8' ? 67 : 60);
            el.w = round2((modules * (el.module || 2)) / d);
            el.barType = bar[1] === 'BE' ? 'EAN13' : (bar[1] === 'B8' ? 'EAN8' : (bar[1] === 'B3' ? 'CODE39' : 'CODE128'));
            if (seg.baseline) el.y = round2(Math.max(0, seg.y - barH) / d);
            let rest = body
                .replace(/\^(BE|B8|B3|BC)[A-Z0-9,]*/, '')
                .replace(/\^FH\\?/g, '')
                .replace(/\^FD[\s\S]*?\^FS/, '');
            if (rest.trim() !== '') return { ok: false, reason: 'barcode extras: ' + rest.trim().slice(0, 40) };
        } else if (gb && !fd) {
            el.type = 'box';
            el.w = round2(+gb[1] / d);
            el.h = round2(+gb[2] / d);
            const t = +gb[3];
            if (gb[4] === 'W' && t >= Math.max(+gb[1], +gb[2])) {
                el.fill = 'white';
                el.thickness = 1;
            } else {
                el.fill = 'none';
                el.thickness = t;
            }
            if (seg.baseline) el.y = round2(Math.max(0, seg.y - +gb[2]) / d);
            const rest = body.replace(/\^GB[\s\S]*?\^FS/, '');
            if (rest.trim() !== '') return { ok: false, reason: 'box extras: ' + rest.trim().slice(0, 40) };
        } else if (fd && font) {
            el.type = 'text';
            el.rotation = font[1].length === 1 && 'NRIB'.indexOf(font[1]) >= 0 ? font[1] : (font[2] && 'NRIB'.indexOf(font[2]) >= 0 ? font[2] : 'N');
            // ^A0R,h,w vs ^AFR,h,w — normalise: groups differ by pattern
            let fh; let fw;
            if (body.match(/\^A0/)) {
                fh = +font[2]; fw = font[3] ? +font[3] : +font[2];
            } else {
                fh = +font[3]; fw = font[4] ? +font[4] : +font[3];
            }
            el.font = fh; el.fontW = fw;
            el.text = unescapeZpl(fd[1]);
            if (fb) {
                el.w = round2(+fb[1] / d);
                el.maxLines = +fb[2];
                el.align = fb[4];
            } else {
                el.align = 'L';
                el.maxLines = 1;
                el.w = round2(Math.min(model.widthMm - el.x, (el.text.length * fh * 0.55) / d));
            }
            el.h = round2((fh * (el.maxLines || 1)) / d);
            // ^FT put the BASELINE at y; the top sits about one ascent
            // (~0.75 em for font 0 / CG Triumvirate) above it.
            if (seg.baseline) el.y = round2(Math.max(0, seg.y - Math.round(fh * 0.75)) / d);
            let rest = body
                .replace(/\^A0[NRIB],\d+(,\d+)?/, '')
                .replace(/\^A[A-Z][NRIB],\d+(,\d+)?/, '')
                .replace(/\^FB\d+,\d+,-?\d+,[LCRJ],\d+/, '')
                .replace(/\^FH\\?/g, '')
                .replace(/\^FD[\s\S]*?\^FS/, '');
            if (rest.trim() !== '') return { ok: false, reason: 'text extras: ' + rest.trim().slice(0, 40) };
        } else {
            return { ok: false, reason: 'unrecognised segment: ' + body.trim().slice(0, 40) };
        }

        model.elements.push(el);
    }

    // Merge background pairs back: a white-filled box immediately followed by
    // an element at the SAME origin is the compiler's opaque-background
    // emission — fold it into `background: true` so the round-trip (and the
    // element count check in verifyRoundTrip) stays exact.
    const merged = [];
    for (let i = 0; i < model.elements.length; i++) {
        const el = model.elements[i];
        const next = model.elements[i + 1];
        if (el.type === 'box' && el.fill === 'white' && next
                && (next.type === 'text' || next.type === 'barcode')
                && Math.abs(el.x - next.x) < 0.05 && Math.abs(el.y - next.y) < 0.05) {
            next.background = true;
            // The box carries the authoritative w/h (text h is re-derived from
            // the font at parse time, which can differ from the drawn box).
            next.w = el.w;
            if (next.type === 'text') next.h = el.h;
            continue;
        }
        merged.push(el);
    }
    model.elements = merged;

    return { ok: true, model };
}

function unescapeZpl(s) {
    // Left-to-right scan: handles our own escapes (\5E, \7E, \\) AND the
    // generic ^FH hex pairs ZebraDesigner emits. \15 is Zebra's firmware
    // remap for the euro sign in every built-in font - decode it to a real
    // one; compile() re-emits it as UTF-8 under ^CI28.
    const str = String(s);
    let out = '';
    for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        if (ch !== '\\') { out += ch; continue; }
        const nxt = str[i + 1];
        if (nxt === '\\') { out += '\\'; i += 1; continue; }
        const hex = str.slice(i + 1, i + 3);
        if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
            const code = parseInt(hex, 16);
            out += code === 0x15 ? '\u20AC' : String.fromCharCode(code);
            i += 2;
            continue;
        }
        out += ch; // lone backslash: keep
    }
    return out;
}

function round2(v) {
    return Math.round(v * 100) / 100;
}

/** Round-trip guard used before every save: compile → parse → compare counts. */
export function verifyRoundTrip(model) {
    const zpl = compile(model);
    const back = parse(zpl, model.dpmm || 8);
    if (!back.ok) return { ok: false, reason: back.reason };
    const a = (model.elements || []).length;
    const b = (back.model.elements || []).length;
    if (a !== b) return { ok: false, reason: 'element count mismatch (' + a + ' vs ' + b + ')' };
    return { ok: true, zpl };
}
